(function () {
/**
 * Simple, elegant form-based filter system
 * Leverages native HTML forms and browser APIs
 */

class CollectionFilters extends HTMLElement {
  /** @type {Function | null} Bound scroll handler function */
  #boundScrollHandler = null;

  constructor() {
    super();
    this.debouncedSubmit = this.debounce(this.onSubmitHandler.bind(this), 500);
    this.isUpdating = false;
  }

  connectedCallback() {
    // Delay to ensure drawer content is available
    setTimeout(() => {
      this.setupForms();
      this.bindEvents();
      this.initMoreFiltersToggle();
    }, 100);
  }

  setupForms() {
    // Find all forms, including those in drawers
    this.forms = this.querySelectorAll('form');
  }

  bindEvents() {
    // Listen to all forms (desktop and mobile)
    this.forms.forEach((form, index) => {
      const formType = form.classList.contains('filter-form-mobile') ? 'mobile' : 'desktop';
      
      form.addEventListener('input', (e) => {
        // Sync the other form before submitting
        this.syncFormsFromSource(form);
        this.debouncedSubmit(e);
      });
      form.addEventListener('change', (e) => {
        // Sync the other form before submitting
        this.syncFormsFromSource(form);
        this.debouncedSubmit(e);
      });
    });

    // Add specific event listener for sort radio buttons to close dropdown
    this.addEventListener('change', (e) => {
      if (e.target.name === 'sort_by' && e.target.type === 'radio') {
        this.closeSortDropdown(e.target);
        // The mobile Sort dropdown sits outside both filter forms, so the form
        // listeners never see it. Mirror the choice into the forms and submit.
        if (!e.target.form) {
          this.querySelectorAll('form input[name="sort_by"]').forEach((input) => {
            input.checked = input.value === e.target.value;
          });
          this.debouncedSubmit(e);
        }
      }
    });
    
    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', (e) => {
      this.onPopState(e);
    });
    
    // Add scroll event listener to close dropdowns when body is scrolled
    this.#setupScrollCloseHandler();
    
    // Listen for clear all clicks
    this.addEventListener('click', (e) => {
      if (e.target.closest('.clear-all-filters')) {
        e.preventDefault();
        this.handleClearAll();
      }
    });
  }

  syncFormsFromSource(sourceForm) {
    const allForms = this.querySelectorAll('form');
    
    allForms.forEach(form => {
      if (form === sourceForm) return; // Skip the source form
      
      // Sync checkboxes and radios
      sourceForm.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(sourceInput => {
        const targetInput = form.querySelector(`input[name="${sourceInput.name}"][value="${sourceInput.value}"]`);
        if (targetInput) {
          targetInput.checked = sourceInput.checked;
        }
      });
      
      // Sync price range inputs
      sourceForm.querySelectorAll('input[type="number"]').forEach(sourceInput => {
        const targetInput = form.querySelector(`input[name="${sourceInput.name}"]`);
        if (targetInput) {
          targetInput.value = sourceInput.value;
        }
      });
      
      // Sync sort_by radio buttons
      const sourceSort = sourceForm.querySelector('input[name="sort_by"]:checked');
      if (sourceSort) {
        const targetSort = form.querySelector(`input[name="sort_by"][value="${sourceSort.value}"]`);
        if (targetSort) {
          targetSort.checked = true;
        }
      }
    });
  }

  onSubmitHandler(event) {
    if (event) event.preventDefault();
    if (this.isUpdating) {
      return;
    }
    
    // Collect data from all forms
    const searchParams = this.buildSearchParams();
    this.renderPage(searchParams, true, 'form-submit');
  }

  buildSearchParams() {
    const params = new URLSearchParams();
    
    // Collect from a single canonical form.
    // Prefer the drawer (mobile) form because it contains the full filter set
    // even when desktop UI only exposes "Quick Filter".
    const desktopForm = this.querySelector('.filter-form-desktop');
    const mobileForm = this.querySelector('.filter-form-mobile');
    const activeForm = mobileForm || desktopForm;
    
    if (activeForm) {
      const formData = new FormData(activeForm);
      
      for (const [key, value] of formData.entries()) {
        if (!value || value.trim() === '') continue;
        
        // Handle price filters - skip if at min/max
        if (key === 'filter.v.price.gte') {
          const rangeInput = activeForm.querySelector('input[type="range"]:first-child');
          const minLimit = parseInt(rangeInput?.min) || 0;
          if (parseInt(value) <= minLimit) continue;
        }
        if (key === 'filter.v.price.lte') {
          const rangeInput = activeForm.querySelector('input[type="range"]:last-child');
          const maxLimit = parseInt(rangeInput?.max) || 1000;
          if (parseInt(value) >= maxLimit) continue;
        }
        
        // Add to params
        if (key === 'sort_by') {
          params.set(key, value);
        } else if (key.startsWith('filter.')) {
          // For filter params, append to allow multiple values
          params.append(key, value);
        }
      }
    }
    
    // Preserve non-filter params from current URL
    const currentParams = new URLSearchParams(window.location.search);
    for (const [key, value] of currentParams.entries()) {
      if (!key.startsWith('filter.') && key !== 'sort_by' && key !== 'page') {
        params.set(key, value);
      }
    }
    
    return params.toString();
  }

  async renderPage(searchParams, pushState = true, source = 'unknown') {
    const timestamp = new Date().toISOString().split('T')[1];
    
    // Prevent concurrent updates
    if (this.isUpdating) {
      return;
    }
    
    this.isUpdating = true;
    
    // Store current state before update
    const openDropdowns = this.getOpenDropdowns();
    const moreFiltersExpanded = this.getMoreFiltersState();
    const openMobileAccordions = this.getOpenMobileAccordions();
    
    // Show loading state
    this.setLoadingState(true);
    
    if (pushState) {
      this.updateURL(searchParams);
    }
    
    try {
      // Only fetch filters, let infinite scroll handle products
      const filtersResponse = await fetch(`${window.location.pathname}?section_id=api--filters&${searchParams}`);
      const filtersHtml = await filtersResponse.text();
      
      // Update filters
      this.updateFilters(filtersHtml, openDropdowns, moreFiltersExpanded, openMobileAccordions);
      
      // Trigger infinite scroll to reload products
      const infiniteScroll = document.querySelector('infinite-scroll-collection');
      if (infiniteScroll && typeof infiniteScroll.reloadProducts === 'function') {
        infiniteScroll.reloadProducts('filter-change');
      } else {
        // Search (and other non-infinite-scroll contexts): update the results grid via section rendering.
        await this.updateSearchResults(searchParams);
      }
      
      // Trigger event for other components
      this.dispatchFilterChangeEvent(searchParams);
      
    } catch (error) {
      console.error('Error updating filters:', error);
    } finally {
      this.setLoadingState(false);
      // Delay resetting the flag to prevent immediate re-triggers
      setTimeout(() => {
        this.isUpdating = false;
      }, 100);
    }
  }

  updateFilters(html, openDropdowns, moreFiltersExpanded, openMobileAccordions = []) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Update mobile filter count
    this.updateMobileFilterCount();
    
    // Update both desktop and mobile forms entirely
    const desktopForm = this.querySelector('.filter-form-desktop');
    const mobileForm = this.querySelector('.filter-form-mobile');
    
    const newDesktopForm = doc.querySelector('.filter-form-desktop');
    const newMobileForm = doc.querySelector('.filter-form-mobile');
    
    // Update desktop form
    if (desktopForm && newDesktopForm) {
      // Get the manager inside
      const manager = desktopForm.querySelector('electric-filter-manager');
      const newManager = newDesktopForm.querySelector('electric-filter-manager');
      
      if (manager && newManager) {
        // Get the filter container that includes the toggle
        const filterContainer = manager.querySelector('.filter-container');
        const newFilterContainer = newManager.querySelector('.filter-container');
        if (filterContainer && newFilterContainer) {
          // Replace the entire filter container to include the toggle
          filterContainer.innerHTML = newFilterContainer.innerHTML;
          // Restore open dropdowns
          openDropdowns.forEach(summaryText => {
            const summary = [...filterContainer.querySelectorAll('summary')].find(s => 
              s.textContent.trim() === summaryText
            );
            if (summary) {
              summary.closest('details').open = true;
            }
          });
          // Re-initialize all components in the container
          this.initializeComponents(filterContainer);
          
          // Restore More Filters state
          const toggle = filterContainer.querySelector('electric-filter-toggle');
          if (toggle && moreFiltersExpanded[0]) {
            setTimeout(() => {
              toggle.isExpanded = true;
              toggle.updateVisibility();
            }, 0);
          }
        } else {
          // Fallback to replacing entire manager
          manager.innerHTML = newManager.innerHTML;
          
          // Restore open dropdowns
          openDropdowns.forEach(summaryText => {
            const summary = [...manager.querySelectorAll('summary')].find(s => 
              s.textContent.trim() === summaryText
            );
            if (summary) {
              summary.closest('details').open = true;
            }
          });
          
          // Re-initialize components
          this.initializeComponents(manager);
          
          // Restore More Filters state
          const toggle = manager.querySelector('electric-filter-toggle');
          if (toggle && moreFiltersExpanded[0]) {
            setTimeout(() => {
              toggle.isExpanded = true;
              toggle.updateVisibility();
            }, 0);
          }
        }
      }
    }
    
    // Update mobile form
    if (mobileForm && newMobileForm) {
      const manager = mobileForm.querySelector('electric-filter-manager');
      const newManager = newMobileForm.querySelector('electric-filter-manager');
      
      if (manager && newManager) {
        manager.innerHTML = newManager.innerHTML;
        
        // Restore mobile accordion states
        openMobileAccordions.forEach(accordionText => {
          const summaries = [...manager.querySelectorAll('summary')];
          const summary = summaries.find(s => s.textContent.trim() === accordionText);
          if (summary) {
            const details = summary.closest('details');
            if (details) {
              details.open = true;
            }
          }
        });
        
        // Re-initialize components
        this.initializeComponents(manager);
        
        // Restore More Filters state for mobile
        const toggle = manager.querySelector('electric-filter-toggle');
        if (toggle && moreFiltersExpanded[1]) {
          toggle.isExpanded = true;
          toggle.updateVisibility();
        }
      }
    }
    // Don't rebind events - they're already bound from initial setup
    // The forms themselves aren't replaced, just their inner content
    // This prevents duplicate event firing when filters are removed
    
    // Update filter removers
    this.updateFilterRemovers(doc);
    
    // Update facet count
    this.updateFacetCount(doc);
  }

  /**
   * Update search results grid when infinite scroll is not present.
   * Falls back to a full page navigation if section rendering fails.
   * @param {string} searchParams
   */
  async updateSearchResults(searchParams) {
    const resultsContainer = document.querySelector('[data-search-results-container]');
    if (!resultsContainer) return;

    const url = `${window.location.pathname}?section_id=api--search-results&${searchParams}`;

    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch search results section: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newContainer = doc.querySelector('[data-search-results-container]');
      if (!newContainer) {
        throw new Error('Missing [data-search-results-container] in section response');
      }

      resultsContainer.innerHTML = newContainer.innerHTML;
    } catch (error) {
      console.error('Error updating search results:', error);

      // Ensure UI is consistent even if section rendering fails.
      const fallbackUrl = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
      window.location.assign(fallbackUrl);
    }
  }

  updateFilterRemovers(doc) {
    // Update desktop removers
    const desktopRemovers = this.querySelector('.desktop-filter-removers');
    const newDesktopRemovers = doc.querySelector('.desktop-filter-removers');
    if (desktopRemovers && newDesktopRemovers) {
      desktopRemovers.innerHTML = newDesktopRemovers.innerHTML;
      // Update visibility
      const hasActiveFilters = desktopRemovers.querySelectorAll('facet-remove').length > 0;
      desktopRemovers.classList.toggle('hidden', !hasActiveFilters);
      desktopRemovers.classList.toggle('flex', hasActiveFilters);
    }
    
    // Update mobile removers  
    const mobileRemovers = this.querySelector('.mobile-filter-removers');
    const newMobileRemovers = doc.querySelector('.mobile-filter-removers');
    if (mobileRemovers && newMobileRemovers) {
      mobileRemovers.innerHTML = newMobileRemovers.innerHTML;
      const hasActiveFilters = mobileRemovers.querySelectorAll('facet-remove').length > 0;
      mobileRemovers.classList.toggle('hidden', !hasActiveFilters);
      mobileRemovers.classList.toggle('flex', hasActiveFilters);
    }
  }

  updateFacetCount(doc) {
    const counts = this.querySelectorAll('electric-filter-count');
    const newCounts = doc.querySelectorAll('electric-filter-count');
    counts.forEach((count, index) => {
      if (newCounts[index]) {
        count.innerHTML = newCounts[index].innerHTML;
      }
    });
  }

  updateMobileFilterCount() {
    // Calculate active filter count from current form state
    const form = this.querySelector('.filter-form-desktop') || this.querySelector('.filter-form-mobile');
    if (!form) return;
    
    let activeCount = 0;
    
    // Count checked checkboxes (excluding availability toggle if needed)
    const checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');
    activeCount += checkboxes.length;
    
    // Check if price range is active
    const priceMinInput = form.querySelector('input[name="filter.v.price.gte"]');
    const priceMaxInput = form.querySelector('input[name="filter.v.price.lte"]');
    if (priceMinInput && priceMaxInput) {
      const rangeMin = form.querySelector('input[type="range"]:first-child');
      const rangeMax = form.querySelector('input[type="range"]:last-child');
      const minLimit = parseInt(rangeMin?.min) || 0;
      const maxLimit = parseInt(rangeMax?.max) || 1000;
      
      const minValue = parseInt(priceMinInput.value) || minLimit;
      const maxValue = parseInt(priceMaxInput.value) || maxLimit;
      
      if (minValue > minLimit || maxValue < maxLimit) {
        activeCount++;
      }
    }
    
    // Update the mobile filter count display
    const mobileCountElement = document.querySelector('.mobile-filter-count');
    if (mobileCountElement) {
      mobileCountElement.textContent = activeCount > 0 ? `(${activeCount})` : '';
    }
  }

  handleClearAll() {
    // Reset all forms
    const currentForms = this.querySelectorAll('form');
    currentForms.forEach(form => {
      // Uncheck all checkboxes and radios
      form.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach(input => {
        input.checked = false;
      });
      
      // Reset price ranges
      const priceRanges = form.querySelectorAll('price-range');
      priceRanges.forEach(range => {
        if (range.reset) range.reset();
      });
    });
    
    // Update mobile filter count immediately
    this.updateMobileFilterCount();
    
    // Render page with empty search params to reload everything
    this.renderPage('', true, 'clear-all');
  }

  onPopState(event) {
    const searchParams = event.state?.searchParams || window.location.search.substring(1);
    
    // Update form inputs based on URL
    this.updateFormsFromURL(searchParams);
    
    // Render page without pushing state
    this.renderPage(searchParams, false, 'popstate');
  }

  updateFormsFromURL(searchParams) {
    const params = new URLSearchParams(searchParams);
    
    const currentForms = this.querySelectorAll('form');
    currentForms.forEach(form => {
      // Reset all inputs first
      form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(input => {
        input.checked = false;
      });
      
      // Set inputs based on URL params
      for (const [key, value] of params.entries()) {
        const inputs = form.querySelectorAll(`input[name="${key}"]`);
        inputs.forEach(input => {
          if (input.type === 'checkbox' || input.type === 'radio') {
            input.checked = input.value === value;
          } else {
            input.value = value;
          }
        });
      }
      
      // Update price ranges
      const priceRanges = form.querySelectorAll('price-range');
      priceRanges.forEach(range => {
        if (range.setFromURLParams) {
          range.setFromURLParams(params);
        }
      });
    });
    
    // Update mobile filter count after forms are updated
    this.updateMobileFilterCount();
  }

  updateURL(searchParams) {
    const newURL = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    if (newURL !== window.location.href) {
      history.pushState({ searchParams }, '', newURL);
    }
  }

  setLoadingState(loading) {
    // Add loading state to opened dropdowns
    this.querySelectorAll('details[open] label').forEach(details => {
      if (loading) {
        details.classList.add('cursor-not-allowed', 'pointer-events-none');
      } else {
        details.classList.remove('cursor-not-allowed', 'pointer-events-none');
      }
    });
    
    // Trigger loading banner if available
    const loadingBanner = document.querySelectorAll('loading-banner[data-target="collection-filters"]');
    for (const banner of loadingBanner) {
      if (loading && typeof banner.startLoading === 'function') {
        banner.startLoading();
      } else if (!loading && typeof banner.completeLoading === 'function') {
        banner.completeLoading();
      }
    }
  }

  getOpenDropdowns() {
    return [...this.querySelectorAll('details[open] summary')].map(s => s.textContent.trim());
  }

  getMoreFiltersState() {
    const toggles = this.querySelectorAll('electric-filter-toggle');
    const states = {};
    toggles.forEach((toggle, index) => {
      states[index] = toggle ? toggle.isExpanded : false;
    });
    return states;
  }

  getOpenMobileAccordions() {
    // Find open accordion details in mobile drawer
    const mobileForm = this.querySelector('.filter-form-mobile');
    if (!mobileForm) return [];
    
    // Get all open accordion details (these use m--accordion component)
    return [...mobileForm.querySelectorAll('details[open]')].map(details => {
      const summary = details.querySelector('summary');
      return summary ? summary.textContent.trim() : null;
    }).filter(Boolean);
  }

  initMoreFiltersToggle() {
    const toggles = this.querySelectorAll('electric-filter-toggle');
    toggles.forEach(toggle => {
      if (!toggle.initialized && toggle.connectedCallback) {
        toggle.connectedCallback();
      }
    });
  }

  initializeComponents(container) {
    // Upgrade custom elements first
    if (window.customElements) {
      customElements.upgrade(container);
    }
    
    // Re-initialize price ranges
    const priceRanges = container.querySelectorAll('price-range');
    priceRanges.forEach(range => {
      // Call connectedCallback to re-initialize
      if (range.connectedCallback) {
        range.connectedCallback();
      }
    });
    
    // Re-initialize filter toggle
    const toggle = container.querySelector('electric-filter-toggle');
    if (toggle) {
      // Reset the toggle component state
      toggle.reset();
      // Trigger connectedCallback to re-setup everything
      if (toggle.connectedCallback) {
        toggle.connectedCallback();
      }
    }
  }

  dispatchFilterChangeEvent(searchParams) {
    window.dispatchEvent(new CustomEvent('filters:changed', {
      detail: { searchParams },
      bubbles: true
    }));
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  disconnectedCallback() {
    // Clean up scroll event listener
    if (this.#boundScrollHandler) {
      document.removeEventListener('scroll', this.#boundScrollHandler, { passive: true });
      this.#boundScrollHandler = null;
    }
  }

  /**
   * Set up scroll event listener to close dropdowns when body is scrolled
   * @private
   */
  #setupScrollCloseHandler() {
    // Bind the handler to maintain proper context
    this.#boundScrollHandler = this.#handleScrollClose.bind(this);
    
    // Add scroll event listener with passive option for better performance
    document.addEventListener('scroll', this.#boundScrollHandler, { passive: true });
  }

  /**
   * Handle scroll events to close open dropdown-disclosure elements
   * @private
   * @param {Event} event - Scroll event
   */
  #handleScrollClose(event) {
    try {
      // Only close dropdowns if the scroll target is the document body or html element
      const scrollTarget = event.target;
      if (scrollTarget !== document.body && scrollTarget !== document.documentElement && scrollTarget !== document) {
        return;
      }

      // Find all open dropdown-disclosure elements - try multiple selectors
      const selectors = [
        'dropdown-disclosure details[open]',
        'electric-filter dropdown-disclosure details[open]',
        'collection-filters dropdown-disclosure details[open]'
      ];
      
      let openDropdowns = [];
      for (const selector of selectors) {
        const found = document.querySelectorAll(selector);
        if (found.length > 0) {
          openDropdowns = found;
          break;
        }
      }
      
      if (openDropdowns.length > 0) {
        // Close each open dropdown
        openDropdowns.forEach(details => {
          details.open = false;
          
          // Dispatch a custom event to notify the dropdown-disclosure component
          const disclosure = details.closest('dropdown-disclosure');
          if (disclosure) {
            disclosure.dispatchEvent(new CustomEvent('dropdown:closed-by-scroll', {
              bubbles: true,
              detail: { reason: 'scroll' }
            }));
          }
        });
      }
        } catch (error) {
      console.error('[CollectionFilters] Error handling scroll close', error);
    }
  }

  /**
   * Close the sort dropdown when a sort option is selected and update display
   * @param {HTMLInputElement} sortRadio - The radio button that was clicked
   */
  closeSortDropdown(sortRadio) {
    try {
      // Find the closest details element (the dropdown)
      const details = sortRadio.closest('details');
      if (details && details.open) {
        details.open = false;
        
        // Dispatch a custom event to notify the dropdown-disclosure component
        const disclosure = details.closest('dropdown-disclosure');
        if (disclosure) {
          disclosure.dispatchEvent(new CustomEvent('dropdown:closed-by-selection', {
            bubbles: true,
            detail: { reason: 'sort-selection' }
          }));
        }
      }

      // Update the selected sort option display
      this.updateSelectedSortDisplay(sortRadio);
    } catch (error) {
      console.error('[CollectionFilters] Error closing sort dropdown', error);
    }
  }

  /**
   * Update the selected sort option display text
   * @param {HTMLInputElement} sortRadio - The radio button that was clicked
   */
  updateSelectedSortDisplay(sortRadio) {
    try {
      // Find the label containing the radio button to get the option text
      const label = sortRadio.closest('label');
      if (!label) return;

      const optionText = label.textContent.trim();

      // Find the span with the selected-sort-option class in the same electric-filter-sort component
      const electricFilterSort = sortRadio.closest('electric-filter-sort');
      if (electricFilterSort) {
        const sortDisplaySpan = electricFilterSort.querySelector('.selected-sort-option');
        if (sortDisplaySpan) {
          sortDisplaySpan.textContent = optionText;
        }
      }

      // Also update the other electric-filter-sort component (mobile/desktop counterpart)
      const allElectricFilterSorts = document.querySelectorAll('electric-filter-sort');
      allElectricFilterSorts.forEach(sortComponent => {
        if (sortComponent !== electricFilterSort) {
          const otherSortDisplaySpan = sortComponent.querySelector('.selected-sort-option');
          if (otherSortDisplaySpan) {
            otherSortDisplaySpan.textContent = optionText;
          }
        }
      });
    } catch (error) {
      console.error('[CollectionFilters] Error updating sort display', error);
    }
  }
}

// Define custom element
customElements.define('collection-filters', CollectionFilters);

/**
 * Facet Remove - Handles individual filter removal
 */
class FacetRemove extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector('a');
    if (!link) return;
    
    link.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Get the URL from the link
      const url = new URL(link.href, window.location.origin);
      const searchParams = url.search.substring(1);
      
      // Find parent collection-filters and trigger update
      const collectionFilters = this.closest('collection-filters');
      if (collectionFilters) {
        collectionFilters.renderPage(searchParams, true, 'facet-remove');
      }
    });
  }
}

customElements.define('facet-remove', FacetRemove);
// Native Price Range Slider with dual overlapped ranges
class PriceRange extends HTMLElement {
  #abortController;
  
  connectedCallback() {
    this.#abortController = new AbortController();
    const signal = { signal: this.#abortController.signal };
    
    // Cache DOM references
    const rangeLowerBound = this.querySelector('input[type="range"].from');
    const rangeHigherBound = this.querySelector('input[type="range"].to');
    const textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]');
    const textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    
    // Ensure elements exist before adding listeners
    if (!rangeLowerBound || !rangeHigherBound || !textInputLowerBound || !textInputHigherBound) {
      console.warn('PriceRange: Missing required elements');
      return;
    }
    
    // Text input handlers - select all on focus
    textInputLowerBound.addEventListener("focus", () => textInputLowerBound.select(), signal);
    textInputHigherBound.addEventListener("focus", () => textInputHigherBound.select(), signal);
    
    // Text input change handlers with validation
    textInputLowerBound.addEventListener("change", (event) => {
      event.preventDefault();
      const max = parseInt(textInputHigherBound.value || event.target.max);
      event.target.value = Math.max(Math.min(parseInt(event.target.value), max - 1), event.target.min || 0);
      rangeLowerBound.value = event.target.value;
      this.#updateRangeVisual(rangeLowerBound, rangeHigherBound);
    }, signal);
    
    textInputHigherBound.addEventListener("change", (event) => {
      event.preventDefault();
      const min = parseInt(textInputLowerBound.value || event.target.min);
      event.target.value = Math.min(Math.max(parseInt(event.target.value), min + 1), event.target.max);
      rangeHigherBound.value = event.target.value;
      this.#updateRangeVisual(rangeLowerBound, rangeHigherBound);
    }, signal);
    
    // Range slider change handlers - update number input and let it handle the event
    rangeLowerBound.addEventListener("change", (event) => {
      event.stopPropagation();
      const max = parseInt(textInputHigherBound.value || event.target.max);
      const newValue = Math.min(parseInt(event.target.value), max - 1);
      textInputLowerBound.value = newValue;
      textInputLowerBound.dispatchEvent(new Event("input", { bubbles: true }));
    }, signal);
    
    rangeHigherBound.addEventListener("change", (event) => {
      event.stopPropagation();
      const min = parseInt(textInputLowerBound.value || event.target.min);
      const newValue = Math.max(parseInt(event.target.value), min + 1);
      textInputHigherBound.value = newValue;
      textInputHigherBound.dispatchEvent(new Event("input", { bubbles: true }));
    }, signal);
    
    // Range slider input handlers - update visual styling and text input values in real-time
    rangeLowerBound.addEventListener("input", (event) => {
      event.target.value = Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1);
      event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      textInputLowerBound.value = event.target.value;
    }, signal);
    
    rangeHigherBound.addEventListener("input", (event) => {
      event.target.value = Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1);
      event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      textInputHigherBound.value = event.target.value;
    }, signal);
    
    // Initialize visual state
    this.#updateRangeVisual(rangeLowerBound, rangeHigherBound);
  }
  
  #updateRangeVisual(rangeLower, rangeHigher) {
    if (!rangeLower || !rangeHigher) return;
    
    const rangeMax = parseInt(rangeLower.max) || 100;
    const minValue = parseInt(rangeLower.value) || 0;
    const maxValue = parseInt(rangeHigher.value) || rangeMax;
    
    const minPercent = (minValue / rangeMax) * 100;
    const maxPercent = (maxValue / rangeMax) * 100;
    
    const container = rangeLower.parentElement;
    if (container) {
      container.style.setProperty("--range-min", `${minPercent}%`);
      container.style.setProperty("--range-max", `${maxPercent}%`);
    }
  }
  
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  
  // Public reset method for filter clearing
  reset() {
    const textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]');
    const textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    const rangeLowerBound = this.querySelector('input[type="range"]:first-child');
    const rangeHigherBound = this.querySelector('input[type="range"]:last-child');
    
    if (textInputLowerBound) textInputLowerBound.value = "";
    if (textInputHigherBound) textInputHigherBound.value = "";
    if (rangeLowerBound) rangeLowerBound.value = rangeLowerBound.min || "0";
    if (rangeHigherBound) rangeHigherBound.value = rangeHigherBound.max || "100";
    
    if (rangeLowerBound && rangeHigherBound) {
      this.#updateRangeVisual(rangeLowerBound, rangeHigherBound);
    }
  }
  
  // Method to set values from URL params (for consistency with old implementation)
  setFromURLParams(urlParams) {
    const minPrice = urlParams.get('filter.v.price.gte');
    const maxPrice = urlParams.get('filter.v.price.lte');
    
    const textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]');
    const textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    const rangeLowerBound = this.querySelector('input[type="range"]:first-child');
    const rangeHigherBound = this.querySelector('input[type="range"]:last-child');
    
    if (minPrice && textInputLowerBound && rangeLowerBound) {
      textInputLowerBound.value = minPrice;
      rangeLowerBound.value = minPrice;
    }
    
    if (maxPrice && textInputHigherBound && rangeHigherBound) {
      textInputHigherBound.value = maxPrice;
      rangeHigherBound.value = maxPrice;
    }
    
    if (rangeLowerBound && rangeHigherBound) {
      this.#updateRangeVisual(rangeLowerBound, rangeHigherBound);
    }
  }
}
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}

/**
 * Simple More/Less Filters Toggle
 */
class ElectricFilterToggle extends HTMLElement {
  /** @type {boolean} Whether the filters are expanded */
  #isExpanded = false;
  
  /** @type {boolean} Whether the component has been initialized */
  #initialized = false;
  
  /** @type {number} Threshold for showing more/less toggle */
  #threshold = 4;
  
  /** @type {string} Text for expanded state */
  #moreText = 'More filters';
  
  /** @type {string} Text for collapsed state */
  #lessText = 'Less filters';
  
  /** @type {Function | null} Click handler reference for cleanup */
  #clickHandler = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.#initialized) return;
    
    this.#threshold = parseInt(this.dataset.threshold) || 4;
    this.#moreText = this.dataset.moreText || 'More filters';
    this.#lessText = this.dataset.lessText || 'Less filters';
    
    // The toggle is clickable itself, not a button inside
    this.style.cursor = 'pointer';
    
    // Remove any existing click listener before adding new one
    if (this.#clickHandler) {
      this.removeEventListener('click', this.#clickHandler);
    }
    this.#clickHandler = this.#handleClick.bind(this);
    this.addEventListener('click', this.#clickHandler);
    
    // Set initial state
    this.#hideEmptyFilters();
    this.#updateVisibility();
    this.#initialized = true;
  }
  
  get filters() {
    // Find the flex container that holds filters
    const container = this.parentElement;
    if (!container) {
      return [];
    }
    
    // Find all electric-filter elements that are siblings
    const allFilters = [];
    for (let child of container.children) {
      if (child.tagName === 'ELECTRIC-FILTER') {
        allFilters.push(child);
      }
    }
    
    return allFilters;
  }

  /**
   * Get only the visible filters (filters that have visible label elements)
   * @returns {Array<HTMLElement>} Array of visible filter elements
   * @private
   */
  get #visibleFilters() {
    return this.filters.filter(filter => {
      // Check if filter is hidden via any mechanism
      const isHiddenByDisplay = filter.style.display === 'none';
      const isHiddenByClass = filter.classList.contains('hidden');
      const isHiddenByAria = filter.getAttribute('aria-hidden') === 'true';
      
      // If filter itself is hidden, it's not visible
      if (isHiddenByDisplay || isHiddenByClass || isHiddenByAria) {
        return false;
      }
      
      // Check if filter has any visible label elements
      const visibleLabels = filter.querySelectorAll('label:not(.hidden)');
      return visibleLabels.length > 0;
    });
  }

  /**
   * Handle click events for the toggle
   * @private
   */
  #handleClick() {
    this.#toggle();
  }

  /**
   * Toggle the expanded state of filters
   * @private
   */
  #toggle() {
    this.#isExpanded = !this.#isExpanded;
    this.#updateVisibility();
  }

  /**
   * Hide filters that have no visible label elements
   * @private
   */
  #hideEmptyFilters() {
    this.filters.forEach(filter => {
      const isListFilter = filter.querySelector('.filter-list-content');
      const visibleLabels = filter.querySelectorAll('.filter-list-content li:not(.hidden)');
      if (visibleLabels.length === 0 && isListFilter) {
        filter.style.display = 'none';
        filter.setAttribute('aria-hidden', 'true');
      } else {
        filter.style.display = '';
        filter.removeAttribute('aria-hidden');
      }
    });
  }

  /**
   * Update the visibility of filters based on expanded state
   * @private
   */
  #updateVisibility() {
    // First hide any filters that have no visible labels
    this.#hideEmptyFilters();
    
    const visibleFilters = this.#visibleFilters;
    
    // Hide button if visible filters count is <= threshold, show if > threshold
    if (visibleFilters.length <= this.#threshold) {
      this.style.display = 'none';
    } else {
      this.style.display = '';
    }
    
    // Show/hide visible filters beyond threshold
    visibleFilters.forEach((filter, index) => {
      if (index >= this.#threshold) {
        filter.style.display = this.#isExpanded ? '' : 'none';
        filter.setAttribute('aria-hidden', !this.#isExpanded);
      }
    });
    
    // Update toggle text - find the text node that's a direct child
    const textNodes = Array.from(this.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
    if (textNodes.length > 0) {
      textNodes[0].textContent = this.#isExpanded ? this.#lessText : this.#moreText;
    } else {
      // If no text node found, update the entire text content before the icon
      const icon = this.querySelector('svg');
      if (icon) {
        const newText = document.createTextNode(this.#isExpanded ? this.#lessText : this.#moreText);
        this.insertBefore(newText, icon);
      }
    }
    
    // Update icon rotation
    const icon = this.querySelector('svg');
    if (icon) {
      icon.style.transform = this.#isExpanded ? 'rotate(45deg)' : 'rotate(0deg)';
      icon.style.transition = 'transform 0.3s';
    }
  }

  /**
   * Public method to toggle filters (for external use)
   */
  toggle() {
    this.#toggle();
  }

  /**
   * Public method to update visibility (for external use)
   */
  updateVisibility() {
    this.#updateVisibility();
  }

  /**
   * Public setter for initialized state (for external use)
   */
  set initialized(value) {
    this.#initialized = value;
  }

  /**
   * Public getter for initialized state (for external use)
   */
  get initialized() {
    return this.#initialized;
  }

  /**
   * Public method to reset the component state
   */
  reset() {
    this.#initialized = false;
    this.#isExpanded = false;
    this.#updateVisibility();
  }

  /**
   * Clean up resources when component is disconnected
   */
  disconnectedCallback() {
    if (this.#clickHandler) {
      this.removeEventListener('click', this.#clickHandler);
      this.#clickHandler = null;
    }
    this.#initialized = false;
  }
}

customElements.define('electric-filter-toggle', ElectricFilterToggle);
})();
(function () {
/**
 * CollectionQuickFilter - Desktop-only quick filter toggle controller
 *
 * Controls the open/closed state of the quick filter panel in `m--filters.liquid`.
 * Uses data attributes to avoid tight coupling to markup structure.
 *
 * Expected markup:
 * <collection-quick-filter>
 *   <button data-quick-filter-toggle>...</button>
 *   <div data-quick-filter-panel class="hidden">...</div>
 * </collection-quick-filter>
 */
class CollectionQuickFilter extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLElement | null} */
  #iconPlus = null;

  /** @type {HTMLElement | null} */
  #iconMinus = null;

  connectedCallback() {
    try {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      const signal = { signal: this.#abortController.signal };

      this.#toggle = this.querySelector('[data-quick-filter-toggle]');
      this.#panel = this.querySelector('[data-quick-filter-panel]');
      this.#iconPlus = this.querySelector('[data-quick-filter-icon-plus]');
      this.#iconMinus = this.querySelector('[data-quick-filter-icon-minus]');

      if (!this.#toggle || !this.#panel) return;

      // Initialize state from attribute (defaults to true if not set)
      const isOpen = this.getAttribute('data-open') !== 'false';
      this.#setOpen(isOpen);

      this.#toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.#setOpen(!this.#isOpen());
      }, signal);
    } catch (error) {
      console.error('CollectionQuickFilter: failed to initialize', error);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @returns {boolean}
   */
  #isOpen() {
    return this.getAttribute('data-open') === 'true';
  }

  /**
   * @param {boolean} open
   */
  #setOpen(open) {
    try {
      this.setAttribute('data-open', open ? 'true' : 'false');

      if (this.#toggle) {
        this.#toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      if (this.#panel) {
        this.#panel.classList.toggle('hidden', !open);
      }

      if (this.#iconPlus) {
        this.#iconPlus.classList.toggle('hidden', open);
      }

      if (this.#iconMinus) {
        this.#iconMinus.classList.toggle('hidden', !open);
      }

      this.dispatchEvent(new CustomEvent('quick-filter:toggle', {
        detail: { open },
        bubbles: true
      }));
    } catch (error) {
      console.error('CollectionQuickFilter: failed to update state', error);
    }
  }
}

if (!customElements.get('collection-quick-filter')) {
  customElements.define('collection-quick-filter', CollectionQuickFilter);
}
})();
(function () {
class InfiniteScrollCollection extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMoreProducts = true;
    this.collectionHandle = this.dataset.collectionHandle;
    
    // Resize handling for dynamic CSS variable updates
    this.onResizeBound = this.handleResize.bind(this);
    this.resizeRafId = null;
    
    // Check for ss=0 query parameter to disable saved state caching
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    this.disableSavedState = params.get('ss') === '0';
    
    // Support removess query parameter to clear saved sessionStorage caches
    // Examples:
    //  - ?removess or ?removess=1 -> clears cache for current collection
    //  - ?removess=all -> clears cache for all collections
    const removeSSParam = params.get('removess');
    const hasRemoveSS = params.has('removess');
    if (hasRemoveSS) {
      const clearAll = removeSSParam === 'all';
      this.clearAllSavedStates(clearAll);
    }
    
    this.productsGrid = this.querySelector('#products-grid');
    this.productsPerPage = this.productsGrid.dataset.productsPerPage || 24;
    this.loadingIndicator = this.querySelector('#loading-indicator');
    this.endOfProducts = this.querySelector('#end-of-products');
    this.smartCrossSellSlider = document.querySelector('#smart-cross-sell');
    this.errorIndicator = this.querySelector('#error-indicator');
    
    // Reference to sentinel element for re-observation after cache restoration
    this.sentinel = null;
    
    // Generate unique key for this collection + filter state
    this.stateKey = this.generateStateKey();
    
    this.init();
  }
  
  /**
   * Lifecycle: element added to DOM
   * Calculate product card image height and set CSS variable; attach resize listener
   */
  connectedCallback() {
    try {
      this.calculateAndSetProductCardHeight();
      this.setupShowMoreEventDelegation();
    } catch (error) {
      console.error('InfiniteScrollCollection: error on connectedCallback height calc', error);
    }
    // Resize listener to keep variable in sync
    window.addEventListener('resize', this.onResizeBound);
  }
  
  /**
   * Set up event delegation for show-more elements
   * Uses event delegation to handle dynamically added show-more elements
   */
  setupShowMoreEventDelegation() {
    // Use event delegation on the products grid to catch all show-more events
    this.productsGrid.addEventListener('show-more:load-requested', async (event) => {
      // Resume infinite scroll and load more products
      this.hasMoreProducts = true;
      
      // Hide the show-more button that was clicked
      const showMore = event.detail.showMoreElement;
      if (showMore) {
        showMore.style.display = 'none';
      }
      
      try {
        await this.loadMoreProducts();
        
        // Remove the show-more element after successful load
        if (showMore && showMore.parentNode) {
          showMore.remove();
        }
        
        // Re-enable sentinel observation now that show-more is gone
        this.#ensureSentinel();
      } catch (error) {
        // Handle error and reset show-more element state
        if (showMore) {
          showMore.style.display = '';
          if (typeof showMore.resetLoadingState === 'function') {
            showMore.resetLoadingState();
          }
          if (typeof showMore.showElement === 'function') {
            showMore.showElement();
          }
        }
        console.error('Failed to load more products from show-more:', error);
      }
    });
  }
  
  generateStateKey() {
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    
    // Remove page parameter as it shouldn't affect the state key
    params.delete('page');
    
    // Sort parameters to ensure consistent state keys
    const sortedParams = Array.from(params.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    
    // Include a layout version to avoid restoring stale cached DOM when the grid structure changes.
    // Example: content cards inserted via collection metafields.
    const layoutKey = (this.dataset.layoutKey || '').trim();
    const layoutHash = layoutKey ? this.#hashString(layoutKey) : 'v0';

    return `infinite-scroll-${this.collectionHandle}-${sortedParams}-${layoutHash}`;
  }

  /**
   * Small, deterministic hash to keep state keys short.
   * FNV-1a 32-bit, encoded as base36.
   * @param {string} str
   * @returns {string}
   */
  #hashString(str) {
    // FNV-1a 32-bit
    let hash = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      // hash *= 16777619 (via bit operations)
      hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
    }
    return hash.toString(36);
  }
  
  init() {
    // Try to restore previous state
    this.restoreState();
    this.#syncPriceText();
    
    // Set up intersection observer for infinite scroll
    this.setupIntersectionObserver();
    
    // Check if we have fewer products than per page limit (no more pages)
    const initialProductCount = this.productsGrid.children.length;
    if (initialProductCount < this.productsPerPage && this.currentPage === 1) {
      this.hasMoreProducts = false;
      this.showEndOfProducts();
      this.showSmartCrossSellSlider();
    }
    
    // Save state when user navigates away
    this.setupStatePreservation();
  }

  /**
   * Ensure product card prices match server-rendered text.
   * Some third-party scripts overwrite price HTML (e.g. dualPrice/finalPrice).
   * @private
   */
  #syncPriceText() {
    try {
      if (!this.productsGrid) return;
      const els = this.productsGrid.querySelectorAll('[data-price-text][data-price]');
      els.forEach((el) => {
        const expected = (el.getAttribute('data-price-text') || '').trim();
        if (!expected) return;
        const current = (el.textContent || '').trim();
        if (current !== expected) {
          el.textContent = expected;
        }
      });
    } catch (error) {
      console.error('InfiniteScrollCollection: failed to sync price text', error);
    }
  }

  restoreState() {
    // Skip state restoration if ss=0 is set
    if (this.disableSavedState) {
      // Clear any existing saved state for this key
      try {
        sessionStorage.removeItem(this.stateKey);
      } catch (error) {
        console.error('Error clearing saved state:', error);
      }
      return;
    }
    
    try {
      const savedState = sessionStorage.getItem(this.stateKey);
      if (savedState) {
        const state = JSON.parse(savedState);
        
        console.log(`Restoring state from ${this.stateKey}:`, {
          currentPage: state.currentPage,
          productsCount: state.productsData ? state.productsData.length : 0,
          hasMore: state.hasMoreProducts,
          timestamp: state.timestamp ? new Date(state.timestamp).toISOString() : 'unknown'
        });
        
        this.currentPage = state.currentPage || 1;
        this.hasMoreProducts = state.hasMoreProducts !== false;
        
        // If we have saved products, restore the complete state
        if (state.productsData && state.productsData.length > 0) {
          console.log(`Restoring ${state.productsData.length} products from session storage`);
          this.#restoreProductsFromData(state.productsData);
          
          // Restore scroll position after a short delay
          setTimeout(() => {
            if (state.scrollPosition) {
              window.scrollTo(0, state.scrollPosition);
            }
          }, 100);
        }
        
        if (!this.hasMoreProducts) {
          this.showEndOfProducts();
          this.showSmartCrossSellSlider();
        }
      } else {
        console.log(`No saved state found for ${this.stateKey}`);
      }
    } catch (error) {
      console.error('Error restoring infinite scroll state:', error);
      // Clear corrupted state
      try {
        sessionStorage.removeItem(this.stateKey);
      } catch (e) {
        console.error('Error clearing corrupted state:', e);
      }
    }
  }
  
  /**
   * Restore products from saved data instead of HTML string
   * @param {Array} productsData - Array of product data objects
   * @private
   */
  #restoreProductsFromData(productsData) {
    if (!Array.isArray(productsData) || productsData.length === 0) return;
    
    // Clear the grid and restore ALL elements from saved state
    this.productsGrid.innerHTML = '';
    
    let showMoreCount = 0;
    let productCount = 0;
    
    // Add ALL elements from saved state (products and show-more buttons)
    productsData.forEach(elementData => {
      const restoredElement = this.#createProductElement(elementData);
      if (restoredElement) {
        this.productsGrid.appendChild(restoredElement);
        
        // Count elements for logging
        if (elementData.tagName === 'show-more') {
          showMoreCount++;
          // Check if show-more indicates we should stop infinite scroll
          this.hasMoreProducts = false;
        } else {
          productCount++;
        }
      }
    });
    
    console.log(`Restored ${productCount} products and ${showMoreCount} show-more buttons from cache`);
    
    // Ensure sentinel is properly positioned after restoration
    this.#ensureSentinel();
  }
  
  /**
   * Create a product element from saved data
   * @param {Object} productData - Product data object
   * @returns {HTMLElement|null} Created product element or null if invalid
   * @private
   */
  #createProductElement(productData) {
    try {
      // Check if productData has the html property
      if (!productData || !productData.html) {
        console.warn('Product data missing html property:', productData);
        return null;
      }
      
      // Create a temporary container to parse the HTML safely
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = productData.html;
      
      const productElement = tempContainer.firstElementChild;
      if (!productElement) {
        console.warn('No product element found in HTML:', productData.html);
        return null;
      }
      
      // Restore any custom properties or data attributes
      if (productData.attributes) {
        Object.entries(productData.attributes).forEach(([key, value]) => {
          productElement.setAttribute(key, value);
        });
      }
      
      return productElement;
    } catch (error) {
      console.error('Error creating product element from saved data:', error, productData);
      return null;
    }
  }

  /**
   * Ensure sentinel exists and is properly positioned at the end of the grid
   * @private
   */
  #ensureSentinel() {
    // Remove existing sentinel if it exists
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.remove();
    }
    
    // Create a new sentinel element
    this.sentinel = document.createElement('div');
    this.sentinel.id = 'scroll-sentinel';
    this.sentinel.setAttribute('data-sentinel', 'true');
    this.sentinel.style.display = 'contents';
    this.sentinel.style.height = '10px';
    this.sentinel.style.width = '100%';
    this.sentinel.style.position = 'relative';
    this.sentinel.style.zIndex = '1';
    this.sentinel.style.pointerEvents = 'none';
    this.sentinel.style.order = '9999'; // Ensure it's always last
    
    // Append sentinel to the products grid (not the component)
    this.productsGrid.appendChild(this.sentinel);
    
    // Observe the sentinel if we have an observer and should continue loading
    if (this.observer && this.hasMoreProducts) {
      // Check if there's a show-more button - if so, don't observe sentinel
      const showMore = this.productsGrid.querySelector('show-more');
      if (!showMore) {
        this.observer.observe(this.sentinel);
        console.log('Sentinel created and observed');
      } else {
        console.log('Sentinel created but not observed (show-more button present)');
      }
    }
  }
  
  /**
   * Re-observe the sentinel element after DOM restoration
   * This ensures the intersection observer works properly after cache restoration
   * @private
   */
  #reobserveSentinel() {
    // Simply ensure the sentinel is properly positioned and observed
    this.#ensureSentinel();
  }
  
  saveState() {
    // Skip state saving if ss=0 is set
    if (this.disableSavedState) {
      return;
    }
    
    try {
      // Collect product data instead of HTML string
      const productsData = this.#collectProductsData();
      
      const state = {
        currentPage: this.currentPage,
        hasMoreProducts: this.hasMoreProducts,
        productsData: productsData,
        scrollPosition: window.scrollY,
        timestamp: Date.now()
      };
      
      console.log(`Saving state for ${this.stateKey}:`, {
        currentPage: state.currentPage,
        productsCount: productsData.length,
        hasMore: state.hasMoreProducts
      });
      
      sessionStorage.setItem(this.stateKey, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving infinite scroll state:', error);
    }
  }
  
  /**
   * Collect product data for state saving
   * @returns {Array} Array of product data objects
   * @private
   */
  #collectProductsData() {
    const productsData = [];
    const allProducts = Array.from(this.productsGrid.children);
    
    // Save ALL elements in the grid except the sentinel
    allProducts.forEach((element) => {
      // Skip the sentinel element
      if (element.hasAttribute('data-sentinel')) {
        return;
      }
      
      try {
        const elementData = {
          html: element.outerHTML,
          tagName: element.tagName.toLowerCase(),
          attributes: {}
        };
        
        // Save important attributes that might be dynamic
        const importantAttributes = ['data-product-id', 'data-variant-id', 'data-collection-id', 'class', 'style'];
        importantAttributes.forEach(attr => {
          if (element.hasAttribute(attr)) {
            elementData.attributes[attr] = element.getAttribute(attr);
          }
        });
        
        productsData.push(elementData);
      } catch (error) {
        console.error('Error collecting element data:', error);
      }
    });
    
    console.log(`Saving complete state: ${productsData.length} total elements (products + show-more buttons, excluding sentinel)`);
    
    return productsData;
  }
  
  setupStatePreservation() {
    // Skip state preservation setup if ss=0 is set
    if (this.disableSavedState) {
      return;
    }
    
    // Save state before page unload
    window.addEventListener('beforeunload', () => {
      this.saveState();
    });
    
    // Save state when clicking on product links
    this.productsGrid.addEventListener('click', (event) => {
      const productLink = event.target.closest('a[href*="/products/"]');
      if (productLink) {
        this.saveState();
      }
    });
    
    // Listen for URL changes to detect filter changes
    // Since filters update the URL, this is the single source of truth
    let currentUrl = window.location.href;
    
    this.urlCheckInterval = setInterval(() => {
      if (window.location.href !== currentUrl) {
        const oldUrl = new URL(currentUrl);
        const newUrl = new URL(window.location.href);
        
        // Only reload if the search parameters actually changed (not just page number)
        const oldParams = new URLSearchParams(oldUrl.search);
        const newParams = new URLSearchParams(newUrl.search);
        oldParams.delete('page');
        newParams.delete('page');
        
        if (oldParams.toString() !== newParams.toString()) {
          currentUrl = window.location.href;
          
          // Clear old state and generate new state key
          try {
            sessionStorage.removeItem(this.stateKey);
          } catch (error) {
            console.error('Error clearing old state on URL change:', error);
          }
          
          this.stateKey = this.generateStateKey();
          
          // Reset component state
          this.currentPage = 1;
          this.hasMoreProducts = true;
          this.isLoading = false;
          
          if (this.endOfProducts) {
            this.endOfProducts.classList.add('hidden');
          }
          
          this.hideError();
          
          // Reload products with new filters
          this.reloadProducts('url-change');
        } else {
          // Just update the current URL reference
          currentUrl = window.location.href;
        }
      }
    }, 100); // Check every 100ms
  }
  
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '2000px', // Increased from 1000px to catch fast scrolling
      threshold: [0, 0.1, 0.5, 1] // Multiple thresholds for better detection
    };
    
    // Create the observer
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMoreProducts) {
          // Check if there's a show-more button - if so, don't auto-load
          const showMore = this.productsGrid.querySelector('show-more');
          if (!showMore) {
            this.loadMoreProducts();
          }
        }
      });
    }, options);
    
    // Create and position the sentinel
    this.#ensureSentinel();
    
    // Add fallback scroll listener for fast scrolling scenarios
    this.setupScrollFallback();
    
    // Add periodic sentinel check as final safety net
    this.setupPeriodicSentinelCheck();
  }
  
  /**
   * Setup fallback scroll listener for fast scrolling detection
   * @private
   */
  setupScrollFallback() {
    let scrollTimeout = null;
    let lastScrollTop = 0;
    let scrollVelocity = 0;
    let lastScrollTime = Date.now();
    
    const handleScroll = () => {
      if (this.isLoading || !this.hasMoreProducts) return;
      
      const currentTime = Date.now();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const timeDelta = currentTime - lastScrollTime;
      
      if (timeDelta > 0) {
        scrollVelocity = Math.abs(scrollTop - lastScrollTop) / timeDelta;
      }
      
      lastScrollTop = scrollTop;
      lastScrollTime = currentTime;
      
      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // For very fast scrolling, use shorter timeout
      const timeoutDelay = scrollVelocity > 2 ? 50 : 100;
      
      // Set a timeout to detect when scrolling stops
      scrollTimeout = setTimeout(() => {
        this.#checkSentinelPosition();
      }, timeoutDelay);
      
      // Immediate check for very fast scrolling - if sentinel is already past viewport
      if (scrollVelocity > 3) {
        this.#checkSentinelPosition(true);
      }
    };
    
    // Use passive scroll listener for performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Store cleanup reference
    this.scrollFallbackHandler = handleScroll;
  }
  
  /**
   * Check sentinel position and trigger load if needed
   * @param {boolean} immediate - Whether to check immediately (bypass velocity check)
   * @private
   */
  #checkSentinelPosition(immediate = false) {
    if (this.isLoading || !this.hasMoreProducts) return;
    
    if (!this.sentinel) return;
    
    // Don't trigger if there's a show-more button
    const showMore = this.productsGrid.querySelector('show-more');
    if (showMore) return;
    
    const sentinelRect = this.sentinel.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // If sentinel is within 500px of viewport, trigger load
    if (sentinelRect.top <= viewportHeight + 500) {
      this.loadMoreProducts();
    }
  }
  
  /**
   * Setup periodic sentinel check as final safety net
   * @private
   */
  setupPeriodicSentinelCheck() {
    // Check every 2 seconds if sentinel might have been missed
    this.sentinelCheckInterval = setInterval(() => {
      if (!this.isLoading && this.hasMoreProducts) {
        this.#checkSentinelPosition();
      }
    }, 2000);
  }
  
  async loadMoreProducts() {
    if (this.isLoading || !this.hasMoreProducts) return;
    
    this.isLoading = true;
    this.showLoading();
    
    try {
      const nextPage = this.currentPage + 1;
      
      // Get current URL parameters to preserve filters
      const currentUrl = new URL(window.location);
      const searchParams = new URLSearchParams(currentUrl.search);
      
      // Add page parameter and view parameter
      searchParams.set('page', nextPage);
      searchParams.set('view', 'products');
      
      const url = `/collections/${this.collectionHandle}?${searchParams.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load products: ${response.status} ${response.statusText}`);
      }
      
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.querySelectorAll('#products-grid > *');
      
      if (newProducts.length === 0) {
        this.hasMoreProducts = false;
        this.showEndOfProducts();
        this.showSmartCrossSellSlider();
      } else {
        // Check if the fetched content contains a <show-more> element
        // Check both direct children and nested elements
        const hasShowMore = Array.from(newProducts).some(element => {
          // Check if the element itself is show-more
          if (element.tagName && element.tagName.toLowerCase() === 'show-more') {
            return true;
          }
          // Also check if show-more is nested inside this element
          return element.querySelector && element.querySelector('show-more') !== null;
        });

                  // If there's a show-more element, stop infinite scroll
          if (hasShowMore) {
            this.hasMoreProducts = false;
            
            // Show-more elements will be handled by event delegation
          }
        
        // Add new products to the grid as DOM nodes
        const addedCards = [];
        newProducts.forEach(product => {
          const clonedProduct = product.cloneNode(true);
          this.productsGrid.appendChild(clonedProduct);
          addedCards.push(clonedProduct);
        });
        this.#syncPriceText();
        
        // Stagger animate the new product cards
        await this.staggerNewProductCards(addedCards);
        
        this.currentPage = nextPage;
        
        // Ensure sentinel is at the end after adding products
        this.#ensureSentinel();
        
        // Check if we got fewer products than expected (last page)
        if (newProducts.length < this.productsPerPage) {
          this.hasMoreProducts = false;
          this.showEndOfProducts();
          this.showSmartCrossSellSlider();
        }
        
        // Save state after successful load
        this.saveState();
        
        // Clear any previous error state
        this.hideError();
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      this.showError(error.message);
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  /**
   * Stagger animate newly added product cards
   * @param {Array} addedCards - Array of newly added product card elements
   * @returns {Promise} Animation completion promise
   * @private
   */
  async staggerNewProductCards(addedCards) {
    // Skip animation if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return Promise.resolve();
    }
    
    if (addedCards.length === 0) return Promise.resolve();
    
    // Set initial state for new product cards
    addedCards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
    });
    
    // Animate new product cards with fast stagger
    const animations = addedCards.map((card, index) => {
      return card.animate([
        { opacity: 0, transform: 'translateY(20px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: 300,
        delay: index * 30, // Much faster: 30ms delay between cards
        easing: 'ease-out',
        fill: 'forwards'
      });
    });
    
    // Wait for all animations to complete
    await Promise.all(animations.map(anim => anim.finished));
    
    // Clean up inline styles
    addedCards.forEach(card => {
      card.style.opacity = '';
      card.style.transform = '';
    });
  }
  
  showLoading() {
    if (!this.loadingIndicator) return;
    this.loadingIndicator.classList.remove('hidden');
  }
  
  hideLoading() {
    if (!this.loadingIndicator) return;
    this.loadingIndicator.classList.add('hidden');
  }
  
  showEndOfProducts() {
    if (!this.endOfProducts) return;
    this.endOfProducts.classList.remove('hidden');
  }

  showSmartCrossSellSlider() {
    if (!this.smartCrossSellSlider) return;
    this.smartCrossSellSlider.classList.remove('hidden');
  }
  
  showError(message) {
    if (this.errorIndicator) {
      const errorMessage = this.errorIndicator.querySelector('.error-message');
      if (errorMessage) {
        errorMessage.textContent = message || 'Failed to load more products. Please try again.';
      }
      this.errorIndicator.classList.remove('hidden');
    }
  }
  
  hideError() {
    if (this.errorIndicator) {
      this.errorIndicator.classList.add('hidden');
    }
  }
  
  retryLoadProducts() {
    this.hideError();
    this.loadMoreProducts();
  }
  
  /**
   * Reload products with current filters
   * Used when filters change to get fresh products
   */
  async reloadProducts(type) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    
    // Reset pagination state
    this.currentPage = 1;
    this.hasMoreProducts = true;
    
    // Step 1: Add opacity-50 and transition to the grid
    this.productsGrid.classList.add('opacity-50', 'transition-opacity', 'duration-300');
    
    try {
      // Get current URL parameters to preserve filters
      const currentUrl = new URL(window.location);
      const searchParams = new URLSearchParams(currentUrl.search);
      
      // Remove page parameter to get first page
      searchParams.delete('page');
      searchParams.set('view', 'products');
      
      const url = `/collections/${this.collectionHandle}?${searchParams.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to reload products: ${response.status} ${response.statusText}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.querySelectorAll('#products-grid > *');
      
      // Step 2: Replace all products in the grid using DOM nodes
      this.productsGrid.innerHTML = '';
      newProducts.forEach(product => {
        this.productsGrid.appendChild(product.cloneNode(true));
      });
      this.#syncPriceText();
      
      // Step 3: Remove opacity classes from new grid
      this.productsGrid.classList.remove('opacity-50', 'transition-opacity', 'duration-300');
      
      // Step 4: Stagger animate product cards
      await this.staggerProductCards();
      
      // Ensure sentinel is properly positioned after reload
      this.#ensureSentinel();
      
      // Check if we have fewer products than per page limit (no more pages)
      if (newProducts.length < this.productsPerPage) {
        this.hasMoreProducts = false;
        this.showEndOfProducts();
        this.showSmartCrossSellSlider();
      } else {
        this.hasMoreProducts = true;
        if (this.endOfProducts) {
          this.endOfProducts.classList.add('hidden');
        }
      }
      
      // Reset to page 1
      this.currentPage = 1;
      
      // Save state after successful reload
      this.saveState();
      
      // Clear any previous error state
      this.hideError();
      
    } catch (error) {
      console.error('Error reloading products:', error);
      this.showError(error.message);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Stagger animate product cards
   * @returns {Promise} Animation completion promise
   * @private
   */
  async staggerProductCards() {
    // Skip animation if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return Promise.resolve();
    }

    const productCards = this.productsGrid.querySelectorAll('product-card');
    
    if (productCards.length === 0) return Promise.resolve();
    
    // Set initial state for product cards
    productCards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
    });
    
    // Animate product cards with fast stagger
    const animations = Array.from(productCards).map((card, index) => {
      return card.animate([
        { opacity: 0, transform: 'translateY(20px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: 300,
        delay: index * 30, // Much faster: 30ms delay between cards
        easing: 'ease-out',
        fill: 'forwards'
      });
    });
    
    // Wait for all animations to complete
    await Promise.all(animations.map(anim => anim.finished));
    
    // Clean up inline styles
    productCards.forEach(card => {
      card.style.opacity = '';
      card.style.transform = '';
    });
  }

  /**
   * Add loading state with opacity-50 and transition
   * @private
   */
  addLoadingState() {
    if (this.productsGrid) {
      this.productsGrid.classList.add('opacity-50', 'transition-opacity', 'duration-300');
    }
  }

  /**
   * Remove loading state
   * @private
   */
  removeLoadingState() {
    if (this.productsGrid) {
      this.productsGrid.classList.remove('opacity-50', 'transition-opacity', 'duration-300');
    }
  }

  /**
   * Clear all saved states for this collection
   * Useful for debugging or when filters are reset
   */
  clearAllSavedStates(clearAll = false) {
    try {
      // Clear all session storage items that start with this collection's prefix
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        if (
          (clearAll && key.startsWith('infinite-scroll-')) ||
          (!clearAll && key.startsWith(`infinite-scroll-${this.collectionHandle}-`))
        ) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing saved states:', error);
    }
  }
  
  /**
   * Public method to manually check sentinel position
   * Useful for debugging or manual triggers
   */
  checkSentinel() {
    this.#checkSentinelPosition(true);
  }
  
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Clean up sentinel element
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.remove();
      this.sentinel = null;
    }
    
    // Clean up URL check interval if it exists
    if (this.urlCheckInterval) {
      clearInterval(this.urlCheckInterval);
    }
    
    // Clean up scroll fallback handler
    if (this.scrollFallbackHandler) {
      window.removeEventListener('scroll', this.scrollFallbackHandler);
      this.scrollFallbackHandler = null;
    }
    
    // Clean up periodic sentinel check interval
    if (this.sentinelCheckInterval) {
      clearInterval(this.sentinelCheckInterval);
      this.sentinelCheckInterval = null;
    }
    
    // Remove resize listener and pending RAF
    window.removeEventListener('resize', this.onResizeBound);
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }
  }
  
  /**
   * Calculate first product card image height and set :root CSS variable
   * Sets `--product-card-height` on documentElement
   */
  calculateAndSetProductCardHeight() {
    try {
      const root = document.documentElement;
      const grid = this.productsGrid || this.querySelector('#products-grid') || this;
      const productCard = grid.querySelector('product-card');
      if (!productCard) return;
      // Try common image selectors inside product card
      const image = productCard.querySelector('picture');
      if (!image) return;
      
      const setVar = () => {
        const height = Math.round((image.getBoundingClientRect().height || image.clientHeight || 0));
        if (height > 0) {
          root.style.setProperty('--product-card-height', `${height}px`);
        }
      };
      
      // If image not laid out yet, wait for it
      if ('complete' in image && !image.complete) {
        image.addEventListener('load', setVar, { once: true });
        image.addEventListener('error', setVar, { once: true });
      }
      // Always attempt immediate set as well
      setVar();
    } catch (error) {
      console.error('InfiniteScrollCollection: failed to set --product-card-height', error);
    }
  }
  
  /**
   * Debounced resize handler (via requestAnimationFrame)
   */
  handleResize() {
    // Skip while pinch-zoomed — layout viewport is unchanged; measuring
    // forces reflows that compound iOS Safari WebContent pressure.
    if (document.documentElement.hasAttribute('data-viewport-zoomed')) return;
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
    }
    this.resizeRafId = requestAnimationFrame(() => {
      this.resizeRafId = null;
      if (document.documentElement.hasAttribute('data-viewport-zoomed')) return;
      this.calculateAndSetProductCardHeight();
    });
  }
}

/**
 * ShowMore Web Component
 * 
 * A button component that triggers the next page load for infinite scroll
 * by updating the URL with the appropriate page parameter.
 * 
 * Can be used as a button itself or as a wrapper for a button element.
 * 
 * @example
 * <!-- As a button itself -->
 * <show-more class="btn">Load More Products</show-more>
 * 
 * @example
 * <!-- As a wrapper -->
 * <show-more>
 *   <button type="button">Load More Products</button>
 * </show-more>
 */
class ShowMore extends HTMLElement {
  /** @type {HTMLElement | null} The clickable element (either this or child button) */
  #clickableElement = null;
  
  /** @type {boolean} Component connection state */
  #isConnected = false;
  
  /** @type {boolean} Loading state to prevent double clicks */
  #isLoading = false;
  
  /** @type {Function | null} Click handler reference for cleanup */
  #clickHandler = null;
  
  /** @type {Function | null} Keydown handler reference for cleanup */
  #keydownHandler = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#isConnected = true;
      this.#initializeButton();
    } catch (error) {
      this.#handleError('Failed to initialize show-more button', error);
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  /**
   * Initialize the button and add event listener
   * @private
   */
  #initializeButton() {
    // Check if there's a button inside, otherwise use this element as the button
    const childButton = this.querySelector('button');
    
    if (childButton) {
      // Use the child button
      this.#clickableElement = childButton;
    } else {
      // Use this element as the button
      this.#clickableElement = this;
      
      // Add button semantics for accessibility
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      
      // Add keyboard support
      this.#keydownHandler = (event) => this.#handleKeydown(event);
      this.addEventListener('keydown', this.#keydownHandler);
    }
    
    this.#clickHandler = (event) => this.#handleClick(event);
    this.#clickableElement.addEventListener('click', this.#clickHandler, { passive: true });
  }

  /**
   * Handle keyboard events for accessibility
   * @param {KeyboardEvent} event - Keyboard event
   * @private
   */
  #handleKeydown(event) {
    // Trigger click on Enter or Space key
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#handleClick(event);
    }
  }

  /**
   * Handle button click to load next page
   * @param {Event} event - Click event
   * @private
   */
  async #handleClick(event) {
    event.preventDefault();
    
    if (this.#isLoading) return;
    
    try {
      this.#isLoading = true;
      this.#updateButtonState(true);
      
      // Dispatch event to trigger infinite scroll to load more products
      this.#dispatchLoadMoreEvent();
      
      // Hide this show-more element after triggering load
      this.style.display = 'none';
      
    } catch (error) {
      this.#handleError('Failed to load more products', error);
      this.#isLoading = false;
      this.#updateButtonState(false);
    }
  }

  /**
   * Update button visual state during loading
   * @param {boolean} isLoading - Loading state
   * @private
   */
  #updateButtonState(isLoading) {
    if (!this.#clickableElement) return;
    
    if (isLoading) {
      // Handle both button and custom element cases
      if (this.#clickableElement.tagName === 'BUTTON') {
        this.#clickableElement.disabled = true;
      } else {
        this.#clickableElement.setAttribute('aria-disabled', 'true');
        this.#clickableElement.style.pointerEvents = 'none';
      }
      
      this.#clickableElement.setAttribute('aria-busy', 'true');
      this.#clickableElement.classList.add('loading');
    } else {
      if (this.#clickableElement.tagName === 'BUTTON') {
        this.#clickableElement.disabled = false;
      } else {
        this.#clickableElement.removeAttribute('aria-disabled');
        this.#clickableElement.style.pointerEvents = '';
      }
      
      this.#clickableElement.removeAttribute('aria-busy');
      this.#clickableElement.classList.remove('loading');
    }
  }

  /**
   * Dispatch custom event when load more is triggered
   * @private
   */
  #dispatchLoadMoreEvent() {
    // Dispatch event that infinite scroll collection will listen for
    this.dispatchEvent(new CustomEvent('show-more:load-requested', {
      detail: { 
        showMoreElement: this,
        timestamp: Date.now()
      },
      bubbles: true
    }));
  }

  /**
   * Handle errors gracefully
   * @param {string} message - Error message
   * @param {Error} error - Original error object
   * @private
   */
  #handleError(message, error) {
    console.error(`ShowMore: ${message}`, error);
    
    this.dispatchEvent(new CustomEvent('show-more:error', {
      detail: { message, error: error.message },
      bubbles: true
    }));
  }

  /**
   * Clean up event listeners
   * @private
   */
  #cleanup() {
    this.#isConnected = false;
    
    if (this.#clickableElement && this.#clickHandler) {
      this.#clickableElement.removeEventListener('click', this.#clickHandler);
      this.#clickHandler = null;
    }
    
    if (this.#keydownHandler) {
      this.removeEventListener('keydown', this.#keydownHandler);
      this.#keydownHandler = null;
    }
    
    this.#clickableElement = null;
  }

  /**
   * Static helper to find all show-more elements
   * @returns {NodeList} All show-more elements
   */
  static findAll() {
    return document.querySelectorAll('show-more');
  }

  /**
   * Static helper to hide all show-more elements
   */
  static hideAll() {
    ShowMore.findAll().forEach(element => {
      element.style.display = 'none';
    });
  }

  /**
   * Public method to reset loading state (called by infinite scroll)
   */
  resetLoadingState() {
    this.#isLoading = false;
    this.#updateButtonState(false);
  }

  /**
   * Public method to show element again (called on error)
   */
  showElement() {
    this.style.display = '';
  }
  
  /**
   * Public method to manually check sentinel position
   * Useful for debugging or manual triggers
   */
  checkSentinel() {
    // Find the infinite scroll collection and call its checkSentinel method
    const infiniteScrollCollection = document.querySelector('infinite-scroll-collection');
    if (infiniteScrollCollection && typeof infiniteScrollCollection.checkSentinel === 'function') {
      infiniteScrollCollection.checkSentinel();
    }
  }
}

customElements.define('infinite-scroll-collection', InfiniteScrollCollection);
customElements.define('show-more', ShowMore);
})();
